import { jsPDF } from 'jspdf';

export const handleDownloadPDF = async (summary, conversation) => {
    if (!summary) return;
    
    try {
      // Create PDF with A4 dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // A4 page dimensions
      const pageWidth = 210;  // mm
      const pageHeight = 297; // mm
      const margin = 20;      // mm
      const usableWidth = pageWidth - (margin * 2);
      
      // Add conversation title
      const title = conversation?.short_note
      
      // Add date
      const date = new Date().toLocaleDateString();
      
      // Parse markdown and convert to PDF
      let y = margin; // Starting y position
      let currentPage = 1;
      
      // Function to add a new page
      const addNewPage = () => {
        pdf.addPage();
        currentPage++;
        y = margin;
        return y;
      };
      
      // Function to check if we need a new page
      const checkNewPage = (heightNeeded) => {
        if (y + heightNeeded > pageHeight - margin) {
          return addNewPage();
        }
        return y;
      };
      
      // Function to process a line of text with proper formatting
      const processLine = (line, fontSize = 11, isBold = false, isItalic = false, leftIndent = 0) => {
        // Check if we need a new page
        y = checkNewPage(8); // 8mm is approx height for a line of text
        
        // Set font style
        let fontStyle = 'normal';
        if (isBold && isItalic) fontStyle = 'bolditalic';
        else if (isBold) fontStyle = 'bold';
        else if (isItalic) fontStyle = 'italic';
        
        pdf.setFont('helvetica', fontStyle);
        pdf.setFontSize(fontSize);
        
        // Split text to fit width
        const textLines = pdf.splitTextToSize(line, usableWidth - leftIndent);
        
        // Add each line
        textLines.forEach(textLine => {
          y = checkNewPage(8);
          pdf.text(textLine, margin + leftIndent, y);
          y += 7; // Line height
        });
        
        return y;
      };
      
      // Add title
      processLine(title, 18, true);
      y += 1; // Extra space after title
      
      // Add generation date
      // processLine(`Generated: ${date}`, 10);
      // y += 5;
      
      // Add a line
      pdf.line(margin, y, pageWidth - margin, y);
      y += 10; // Space after line
      
      // Process markdown content
      const lines = summary.split('\n');
      let inList = false;
      let listIndent = 0;
      
      for (let i = 0; i < lines.length; i++) {
        let line = lines[i].trim();
        
        // Skip empty lines
        if (!line) {
          y += 5;
          continue;
        }
        
        // Headers
        if (line.startsWith('# ')) {
          processLine(line.substring(2), 16, true);
          y += 5;
        }
        else if (line.startsWith('## ')) {
          processLine(line.substring(3), 14, true);
          y += 4;
        }
        else if (line.startsWith('### ')) {
          processLine(line.substring(4), 12, true);
          y += 3;
        }
        
        // Lines with partial bold text (e.g., "**Patient:** Herr Müller")
        else if (line.includes('**') && line.indexOf('**') !== line.lastIndexOf('**')) {
          // Check if it's a label-value pattern like "**Patient:** Herr Müller"
          const labelMatch = line.match(/^\*\*(.*?)\*\*\s*:/);
          
          if (labelMatch) {
            // It's a label-value pattern
            const label = labelMatch[1];
            const restOfLine = line.substring(line.indexOf(':') + 1).trim();
            
            // Set bold font for the label
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            pdf.text(`${label}:`, margin, y);
            
            // Calculate width of the bold part
            const labelWidth = pdf.getTextWidth(`${label}:`);
            
            // Set normal font for the value
            pdf.setFont('helvetica', 'normal');
            
            // Split the rest of the line to fit
            const valueLines = pdf.splitTextToSize(restOfLine, usableWidth - labelWidth - 5);
            
            // Add the first line of the value next to the label
            if (valueLines.length > 0) {
              pdf.text(valueLines[0], margin + labelWidth + 2, y);
            }
            
            // Add any remaining lines of the value with proper indentation
            if (valueLines.length > 1) {
              for (let j = 1; j < valueLines.length; j++) {
                y += 7;
                y = checkNewPage(8);
                pdf.text(valueLines[j], margin + labelWidth + 2, y);
              }
            }
            
            y += 7; // Move to next line
          } else {
            // Handle other types of partial bold text
            // This is a simplified approach - for complex mixed formatting, 
            // a more sophisticated parser would be needed
            const parts = line.split('**');
            let xPos = margin;
            let isInBold = false;
            
            for (let j = 0; j < parts.length; j++) {
              if (parts[j]) {
                pdf.setFont('helvetica', isInBold ? 'bold' : 'normal');
                pdf.setFontSize(11);
                
                const textWidth = pdf.getTextWidth(parts[j]);
                
                // Check if this part would exceed the line width
                if (xPos + textWidth > pageWidth - margin) {
                  y += 7;
                  y = checkNewPage(8);
                  xPos = margin;
                }
                
                pdf.text(parts[j], xPos, y);
                xPos += textWidth;
              }
              isInBold = !isInBold;
            }
            
            y += 7; // Move to next line
          }
        }
        
        // Bold text (entire line)
        else if (line.startsWith('**') && line.endsWith('**')) {
          processLine(line.substring(2, line.length - 2), 11, true);
        }
        
        // Italic text
        else if (line.startsWith('*') && line.endsWith('*')) {
          processLine(line.substring(1, line.length - 1), 11, false, true);
        }
        
        // List items
        else if (line.startsWith('- ')) {
          if (!inList) {
            inList = true;
            y += 2; // Extra space before list
          }
          processLine('• ' + line.substring(2), 11, false, false, 5);
        }
        
        // Regular text
        else {
          if (inList) {
            inList = false;
            y += 2; // Extra space after list
          }
          processLine(line);
        }
      }
      
      // Add page numbers
      for (let i = 1; i <= currentPage; i++) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.text(`Page ${i} of ${currentPage}`, pageWidth - margin - 25, pageHeight - margin);
      }
      
      // Generate filename with date
      const filename = `Summary_${date.replace(/\//g, '-')}.pdf`;
      
      // Save the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };